import * as XLSX from "xlsx";
import adviserMap from "./quilterAdviserMap.json";

export const SUSPENSE_TABLE_HEADERS = ["Index", "Adviser", "LinkedAdviser", "Date", "ClientName", "PolicyAccountNumber", "PolicyProvider", "SuspenseAmount", "SuspenseCharge"];

const uniqueHeaders = (headerRow) => {
  const counts = new Map();
  return headerRow.map((value, index) => {
    const fallback = index === 2 ? "Clearance Status" : `Column ${index + 1}`;
    const base = String(value ?? "").trim() || fallback;
    const count = (counts.get(base) || 0) + 1;
    counts.set(base, count);
    return count === 1 ? base : `${base} (${count})`;
  });
};

const formatDate = (value) => {
  const text = String(value ?? "").trim();
  const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (!match) return text;
  const year = match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);
  return new Date(year, Number(match[1]) - 1, Number(match[2])).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

export const normaliseSuspenseRows = (rows = []) => rows.map((row, index) => {
  const sourceAdviserName = String(row.ADVISER ?? row.SourceAdviserName ?? row.Adviser ?? "").trim();
  const mappedAdviserName = adviserMap[sourceAdviserName] || row.MappedAdviserName || sourceAdviserName;
  return {
    Index: String(index + 1),
    Adviser: mappedAdviserName,
    LinkedAdviser: String(row.LinkedAdviser ?? "").trim(),
    SourceAdviserName: sourceAdviserName,
    MappedAdviserName: mappedAdviserName,
    Date: formatDate(row.DATE ?? row.Date),
    ClearanceStatus: formatDate(row["Clearance Status"] ?? row["Column 3"] ?? row.ClearanceStatus),
    ClientName: String(row.CLIENT ?? row.ClientName ?? "").trim(),
    PolicyAccountNumber: String(row["POLICY NUMBER"] ?? row.PolicyAccountNumber ?? "").trim(),
    PolicyProvider: String(row.PAYER ?? row["POLICY PROVIDER"] ?? row.PolicyProvider ?? "").trim(),
    SuspenseAmount: String(row["SUSPENSE AMOUNT"] ?? row.SuspenseAmount ?? "").trim(),
    SuspenseCharge: String(row["SUSPENSE CHARGE"] ?? row.SuspenseCharge ?? "").trim(),
  };
});

export const matrixToSuspenseStatement = (matrix = []) => {
  const populatedRows = matrix.filter((row) => Array.isArray(row) && row.some((cell) => String(cell ?? "").trim() !== ""));
  if (populatedRows.length < 2) throw new Error("The selected file does not contain any statement entries.");
  const sourceHeaders = uniqueHeaders(populatedRows[0]);
  const sourceRows = populatedRows.slice(1).map((row) => sourceHeaders.reduce((entry, header, index) => {
    entry[header] = String(row[index] ?? "").trim();
    return entry;
  }, {}));
  return { sourceHeaders, headers: SUSPENSE_TABLE_HEADERS, rows: normaliseSuspenseRows(sourceRows) };
};

export const parseSuspenseStatementFile = async (file) => {
  if (!file) throw new Error("Select a statement to upload.");
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (!["csv", "xlsx", "xls"].includes(extension)) throw new Error("Upload a CSV or Excel file.");
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: false });
  const firstSheet = workbook.SheetNames[0];
  if (!firstSheet) throw new Error("The selected workbook does not contain a sheet.");
  return matrixToSuspenseStatement(XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], {
    header: 1, defval: "", raw: false, dateNF: "dd/mm/yyyy",
  }));
};
